import React, { createContext, useContext, useState, useCallback } from 'react';
import client from '../api/client';

interface User {
    id: number;
    name: string;
    email: string;
    role: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    login: (email: string, password: string, deviceName?: string) => Promise<void>;
    logout: () => Promise<void>;
    isAuthenticated: boolean;
    loading: boolean;
}

const TOKEN_KEY = 'metrosafe_token';
const USER_KEY = 'metrosafe_user';

const AuthContext = createContext<AuthContextType | null>(null);

function defaultDeviceName(): string {
    if (typeof navigator === 'undefined') return 'dashboard';
    const ua = navigator.userAgent || 'dashboard';
    return `dashboard-${ua.slice(0, 80)}`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [token, setToken] = useState<string | null>(
        () => localStorage.getItem(TOKEN_KEY) ?? localStorage.getItem('token'),
    );
    const [user, setUser] = useState<User | null>(() => {
        const stored = localStorage.getItem(USER_KEY) ?? localStorage.getItem('user');
        return stored ? JSON.parse(stored) : null;
    });
    const [loading, setLoading] = useState(false);

    const login = useCallback(async (email: string, password: string, deviceName?: string) => {
        setLoading(true);
        try {
            const { data } = await client.post('/auth/login', {
                email,
                password,
                device_name: deviceName ?? defaultDeviceName(),
            });
            localStorage.setItem(TOKEN_KEY, data.token);
            localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            setToken(data.token);
            setUser(data.user);
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await client.post('/auth/logout');
        } catch {
            // ignore — token may already be invalid
        } finally {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
        }
    }, []);

    return (
        <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
